import React, { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ScrollView, StyleSheet } from 'react-native';
import { FlatList } from 'react-native-gesture-handler';

import { PlayerInfoWithProjectionsFragment, PlayerProjectionFragment } from '@/api/events/query.generated';
import { EventInfo } from '@/api/events/types/types';
import FireIcon from '@/assets/icons/fire';
import { ChipButton } from '@/components/ChipButton';
import { SizedBox as SizeBoxComponent } from '@/components/SizedBox';
import { eventUtils } from '@/feature/betslip-pickem/utils/event-utils';
import { usePlayerFilters } from '@/feature/lobby/hooks/use-player-filters';
import { common } from '@/styles/styles';
import { showScrollIndicator } from '@/utils/constants-platform-specific';

import { usePopularProjectionsPlayers } from '../hooks/use-lobby-data';
import { POPULAR_PLAYERS_FILTER } from '../utils/players';

const SizedBox = () => <SizeBoxComponent value={8} />;

/**
 * Minimum required number of popular players to display the "Popular" filter
 */
const MIN_REQUIRED_POPULAR_PLAYERS = 2;

export const PlayerProjectionsFilters = ({ events }: { events: EventInfo[] }) => {
    const { t } = useTranslation('betslip_pickem');

    const allUniqueProjections = useMemo(() => {
        const allPlayers = events?.reduce((acc: PlayerInfoWithProjectionsFragment[], event) => {
            return [...acc, ...eventUtils.getAllPlayers(event)];
        }, []);

        const allProjections = allPlayers.reduce(
            (acc: PlayerProjectionFragment[], player: PlayerInfoWithProjectionsFragment) => [
                ...acc,
                ...player.projections,
            ],
            []
        );
        const uniqueProjections = [...new Map(allProjections.map(item => [item.key, item])).values()];

        return uniqueProjections.sort((proj1, proj2) => proj1.order - proj2.order);
    }, [events]);

    const { playerProjectionFilter, setPlayerProjectionFilter, scrollToFilters } = usePlayerFilters();

    const popularProjectionsPlayers = usePopularProjectionsPlayers(events);

    // Create the data for FlatList with the Popular filter added if we have popular players)
    const filtersData = useMemo(() => {
        const popularPlayersFilter = {
            key: POPULAR_PLAYERS_FILTER,
            label: t('popular'),
        } as PlayerProjectionFragment;

        if (popularProjectionsPlayers === undefined) {
            //While popular data loading, return empty to prevent flickers
            return [];
        }

        return popularProjectionsPlayers.length >= MIN_REQUIRED_POPULAR_PLAYERS
            ? [popularPlayersFilter, ...allUniqueProjections]
            : allUniqueProjections;
    }, [allUniqueProjections, popularProjectionsPlayers, t]);

    useEffect(() => {
        //Data is not ready yet
        if (!filtersData || filtersData.length === 0) {
            return;
        }
        //If the filter is already set, we don't need to set it again
        if (playerProjectionFilter) {
            return;
        }
        //If the filter is not set, we set it to the first one
        setPlayerProjectionFilter(filtersData[0].key, filtersData[0].label);
    }, [filtersData, playerProjectionFilter, setPlayerProjectionFilter]);

    const renderItem = useCallback(
        ({ item }: { item: PlayerProjectionFragment }) => {
            return (
                <ChipButton
                    key={item.label}
                    label={item.label ?? ''}
                    active={playerProjectionFilter === item.key}
                    onPress={() => {
                        // when changing filters, we must scroll to the top of the list where filters are displayed
                        scrollToFilters();
                        setPlayerProjectionFilter(item.key ?? '', item.label ?? '');
                    }}
                    testID={`projectionsChipButton-${item.key}`}
                    iconLeft={item.key === POPULAR_PLAYERS_FILTER ? <FireIcon /> : undefined}
                />
            );
        },
        [playerProjectionFilter, scrollToFilters, setPlayerProjectionFilter]
    );

    /*
    Nesting the FlatList inside a ScrollView to allow horizontal scrolling - the "spring" effect, even when the list of item is short and doesn't require scrolling
    If they have different scroll directions, there will be no warning.
    Also it fixed the swipe gesture in the Chip bar that allows the user to navigate between tabs
    */

    if (filtersData.length === 0) {
        // there's a bug in section list where it doesn't render the next page when scrolling
        // not rendering the list when there's no data somehow fixes it: https://github.com/facebook/react-native/issues/39421
        return null;
    }

    return (
        <ScrollView style={common.flex} scrollEnabled={false}>
            <FlatList
                horizontal
                data={filtersData}
                ListFooterComponent={<SizeBoxComponent value={16} />}
                ListHeaderComponent={<SizeBoxComponent value={16} />}
                ItemSeparatorComponent={SizedBox}
                showsHorizontalScrollIndicator={showScrollIndicator}
                contentContainerStyle={styles.flatListStyle}
                renderItem={renderItem}
            />
        </ScrollView>
    );
};

const styles = StyleSheet.create({
    flatListStyle: {
        paddingVertical: 16,
        minWidth: '100%',
    },
});
